import type { Agent, AgentsData, Permission } from '@jarvis/shared';
import { Icon } from '../components/Icon';
import { Resource } from '../components/Resource';
import { CardHead, DemoBadge, PageHead } from '../components/ui';
import { fmt } from '../lib/format';
import { JOB_STATUS } from '../lib/jobs';

const PERM: Record<Permission, { cls: string; text: string }> = {
  yes: { cls: 'yes', text: 'Permitido' },
  ask: { cls: 'ask', text: 'Só com sua confirmação' },
  no: { cls: 'no', text: 'Bloqueado' }
};

function AgentCard({ a }: { a: Agent }) {
  const perm = (key: keyof Agent['perms'], label: string) => (
    <div className={`perm ${PERM[a.perms[key]].cls}`}><b>{label}</b>{PERM[a.perms[key]].text}</div>
  );
  return (
    <article className="card agent">
      <div className="agent-top">
        <div className="agent-avatar"><Icon name={a.icon} /></div>
        <div className="row-main"><h3>{a.name}</h3><p className="row-sub">{a.goal}</p></div>
      </div>
      <div className="perm-grid">{perm('read', 'Ler')}{perm('prepare', 'Preparar')}{perm('execute', 'Executar')}</div>
      <dl className="kv"><dt>Gatilho</dt><dd>{a.trigger}</dd><dt>Limite</dt><dd>{a.budget}</dd><dt>Estado</dt><dd>{a.status}</dd></dl>
      <div className="chips"><button type="button" className="btn btn-sm" disabled title="Agentes ainda não executam (Etapa 8)"><Icon name="play" />Ativar</button><DemoBadge /></div>
    </article>
  );
}

function AgentsBody({ d }: { d: AgentsData }) {
  return (
    <div className="stack">
      <div className="agents">{d.agents.map((a) => <AgentCard key={a.id} a={a} />)}</div>
      <section className="card">
        <CardHead icon="layers" title="Tarefas em segundo plano" extra={<>
          <DemoBadge />
          <button type="button" className="btn btn-sm" disabled title="Disponível quando houver execução real (Etapa 8)"><Icon name="pause" />Pausar tudo</button>
        </>} />
        <div className="table-wrap"><table className="table">
          <thead><tr><th>Tarefa</th><th>Agente</th><th>Estado</th><th>Início</th><th>Consumo</th></tr></thead>
          <tbody>{d.jobs.map((j) => (
            <tr key={j.id}>
              <td><div className="row-title">{j.task}</div><div className="row-sub mono">{j.id}</div>{j.error && <div className="row-sub">{j.error}</div>}</td>
              <td>{j.agent}</td>
              <td><span className={`badge ${JOB_STATUS[j.status].cls}`}>{JOB_STATUS[j.status].label}</span></td>
              <td className="mono">{j.started ? fmt.relative(j.started) : '—'}</td>
              <td className="mono">{j.cost}</td>
            </tr>))}
          </tbody>
        </table></div>
      </section>
      <section className="card">
        <CardHead icon="file" title="Registro de atividade" extra={<DemoBadge />} />
        <ul className="list">{d.log.map((l) => <li key={l.at + l.text} className="row"><span className="row-meta">{fmt.relative(l.at)}</span><div className="row-main">{l.text}</div></li>)}</ul>
      </section>
    </div>
  );
}

export function Agents() {
  return (
    <div className="page">
      <PageHead title="Agentes" lead="Cada agente tem objetivo, gatilho, limite de consumo e permissões separadas para ler, preparar e executar. Nenhum agente roda nesta etapa." />
      <Resource name="agents" skeleton="cards" skeletonCount={4}
        empty={{ icon: 'agent', title: 'Nenhum agente configurado', text: 'Agentes e tarefas em segundo plano chegam na Etapa 8, depois das integrações de leitura, com limite de custo, registro de atividade e botão para pausar tudo.' }}>
        {(d) => <AgentsBody d={d} />}
      </Resource>
    </div>
  );
}
